import { Injectable } from '@angular/core';
import { Query, filterNilValue } from '@datorama/akita';
import { NGXLogger } from 'ngx-logger';
import { Observable, combineLatest, fromEvent, of } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';

import { GseApiConfig } from 'gse-api';
import { User } from 'models';
import { ThemeMode, UserUiSettingsSet } from 'src/app/models';

import { GenericFont } from 'common';
import { assert } from 'common';
import { AppConfigQuery } from 'src/app/modules/app-config';
import { SupportedFeaturesService } from 'src/app/services/supported-features.service';
import { ApplicationState, ApplicationStore } from './application.store';
import {
  FavoriteDatabaseSearches,
  FavoriteSearch,
} from './favorite-searches.service';
import { OpenDocumentSource } from './open-document-source';

/**
 * Application query.
 */
@Injectable({ providedIn: 'root' })
export class ApplicationQuery extends Query<ApplicationState> {
  /** Observable API version. */
  apiVersion$ = this.select('apiVersion');
  /** Observable of archive history results per page state. */
  archiveHistoryResultsPerPage$ = this.select('archiveHistoryResultsPerPage');
  /** Observable of whether the archive navigation section is expanded. */
  archiveNavSectionExpanded$ = this.select('archiveNavSectionExpanded');
  /** Observable of archive results per page state. */
  archiveResultsPerPage$ = this.select('archiveResultsPerPage');
  /** Observable of whether to automatically resize text areas to fit content on focus. */
  autoSizeTextAreasToContentOnFocus$ = this.select(
    'autoSizeTextAreasToContentOnFocus'
  );
  /** Observable of the current Language. */
  currentLanguage$ = this.select('currentLanguage');
  /** Observable array of the user's custom stamps. */
  customStamps$ = this.select('customStamps');
  /** Observable of dark mode enabled state. */
  darkMode$: Observable<boolean>;
  /** Observable of double click to open docs enabled state. */
  doubleClickToOpenDocuments$ = this.select('doubleClickOpen');
  /** Observable of enabled task searches state. */
  enabledTaskSearches$ = this.select('enabledTasksList');
  /** Observable array of filenames previously used in enhanced export. */
  enhancedExportFilenameHistory$ = this.select('enhancedExportFilenameHistory');
  /** Observable of whether the favorite search navigation section is expanded. */
  favoriteSearchNavSectionExpanded$ = this.select('favoriteSearchesExpanded');
  /** Observable of GlobalSerach Extensions configuration. */
  gseConfig$ = this.select('gseApiConfig');
  /** Observable of whether to hide archives with no results in a multi-archive search. */
  hideArchivesWithNoResults$ = this.select('hideArchivesWithNoResults');
  /** Observable of whether the inbox navigation section is expanded. */
  inboxNavSectionExpanded$ = this.select('inboxNavSectionExpanded');
  /** Observable of whether the indexer sidebar should be open. */
  indexerSidebarOpen$ = this.select('indexerSidebarOpen');
  /** Observable of whether to reload fields on save. */
  indexerReloadAfterSave$ = this.select('indexerReloadAfterSave');
  /** Observable instance URL. */
  instanceUrl$ = this.select('instanceUrl');
  /** Observable of whether to use the local ocr keyfree. */
  keyfreeUseOcr$ = this.select('keyfreeUseOcr');
  /** Observable of whether to automatically enable OCR when no text layer words are found. */
  keyfreeAutoOcr$ = this.select('keyfreeAutoOcr');
  /** Observable of whether the left sidebar navigation section is hidden. */
  leftSidebarNavHidden$ = this.select('leftSidebarNavHidden');
  /** Obserable of system notifications enabled state. */
  notifications$ = this.select('notifications');
  /** Observable position for where notifications should be displayed. */
  notificationPosition$ = this.select('notificationPosition');
  /**
   * Observable of the open document.
   * @todo this is currently only set via `ApplicationService.setOpenDocument()` when "preview" is used.
   */
  openDocument$ = this.select('openDocument');
  /** Observable of the PDF Preview URL. */
  pdfPreviewUrl$ = this.select('pdfPreviewUrl');
  /**
   * Observable of if annotations should be rendered in the modify thumbnailer.
   *
   * This is currently not used and is set to false. It should be tied to a user setting
   * if we decide to revisit rendering annotations in the thumbnailer.
   */
  renderAnnotationsInModifyThumbnailer$ = of(false);
  /** Observable of the visibility of the right sidebar state. */
  rightSidebarOpen$ = this.select('rightSidebarOpen');
  /** Observable of search count enabled. */
  searchCountEnabled$ = this.select('searchCountEnabled');
  /** Observable of the database ID for which the search store is currently loaded. */
  searchStoreDatabaseId$ = this.select('searchStoreDatabaseId');
  /** Observable of show domain in history results state. */
  showDomainInHistoryResults$ = this.select('showDomainInHistoryResults');
  /** Observable of the show favorite searches state. */
  showFavoriteSearches$ = this.select('showFavoriteSearches');
  /** Observable of whether to show table field columns in the grid. */
  showTableFieldColumnsInGrid$ = this.select('showTableFieldColumnsInGrid');
  /** Observable of whether to show versions archive. */
  showVersionsArchive$ = this.select('showVersionsArchive');
  /** Observable of whether to sort results archives by results count. */
  sortResultsArchivesByResultsCount$ = this.select(
    'sortResultsArchivesByResultsCount'
  );
  /** Observable of whether the task search section is expanded. */
  taskSearchSectionExpanded$ = this.select('taskSearchSectionExpanded');
  /** Observable theme mode. */
  themeMode$ = this.select('themeMode');
  /** Observable of compact mode enabled. */
  useCompactMode$ = this.select('useCompactMode');
  /** Observable of authenticated users token. */
  token$ = this.select((state) => state.user?.token);
  /** Observable of whether to use the compact table field grid. */
  useCompactTableFieldGrid$ = this.select('useCompactTableFieldGrid');
  /** Observable of the state overrides to the user action buttons. */
  userActionButtonStateOverrides$ = this.select(
    'userActionButtonStateOverrides'
  );
  /** Obserable of authenticated user. */
  user$ = this.select('user');
  /** Observable of if a user is currently logged in. */
  userLoggedIn$ = this.user$.pipe(distinctUntilChanged(), filterNilValue());
  /** Observable of which user settings have been set by the user. */
  userUiSettingsSetByUser$ = this.select('userUiSettingsSetByUser');
  /** Observable of whether the internal viewer should automatically save changes. */
  viewerAutoSave$ = this.select('viewerAutoSave');
  /** Observable of whether to use the internal viewer. */
  viewerUseInternal$ = this.select('viewerUseInternal');
  /** Observable of whether to show table cell editor buttons. */
  showTableCellEditorButtons$ = this.select('showTableCellEditorButtons');

  constructor(
    private logger: NGXLogger,
    private appConfigQuery: AppConfigQuery,
    protected store: ApplicationStore
  ) {
    super(store);

    this.createDarkModeListener();
  }

  /**
   * Get setting for if a documents should always be opened in a new tab.
   *
   * @returns True if documents should always open in a new tab.
   */
  get alwaysOpenNewTab(): boolean {
    return this.getValue().alwaysOpenNewTab;
  }

  /**
   * Get the default fill (background) color for note annotations.
   *
   * @returns The default note annotation fill color.
   */
  get annotationNoteDefaultFillColor(): string {
    return this.getValue().annotationNoteDefaultFillColor;
  }

  /**
   * Get the default outline opacity for note annotations.
   *
   * @returns The default note outline opacity.
   */
  get annotationNoteDefaultOutlineOpacity(): number {
    return this.getValue().annotationNoteDefaultOutlineOpacity;
  }

  /**
   * Get the default font name for note annotations.
   *
   * @returns The default note font name.
   */
  get annotationNoteDefaultFontName(): GenericFont {
    return this.getValue().annotationNoteDefaultFontName;
  }

  /**
   * Get the default font size for note annotations.
   *
   * @returns The default note text size.
   */
  get annotationNoteDefaultFontSize(): number {
    return this.getValue().annotationNoteDefaultFontSize;
  }

  /**
   * Get the default stamp color.
   *
   * @returns The default stamp color.
   */
  get annotationStampDefaultColor(): string {
    return this.getValue().annotationStampDefaultColor;
  }

  /**
   * Get the default opacity.
   *
   * @returns The default stamp opacity.
   */
  get annotationStampDefaultOpacity(): number {
    return this.getValue().annotationStampDefaultOpacity;
  }

  /**
   * Get the default stamp thickness.
   *
   * @returns The default stamp thickness.
   */
  get annotationStampDefaultThickness(): number {
    return this.getValue().annotationStampDefaultThickness;
  }

  /**
   * Get the Square9 API URL.
   *
   * @returns A string representing the Square 9 API URL.
   */
  get apiUrl(): string {
    return `${this.instanceUrl}/square9api/api`;
  }

  /**
   * Gets the current API version.
   *
   * @returns An API version string.
   */
  get apiVersion(): string {
    return this.getValue().apiVersion;
  }

  /**
   * Get the archive results per page setting.
   *
   * @returns A number.
   */
  get archiveResultsPerPage(): number {
    return this.getValue().archiveResultsPerPage;
  }

  /**
   * Get the auto open first table field on indexer load setting.
   *
   * @returns True if the first table field should open on indexer load.
   */
  get autoOpenFirstTableFieldOnIndexerLoad(): boolean {
    return this.getValue().autoOpenFirstTableFieldOnIndexerLoad;
  }

  /**
   * Gets the {@link ApplicationState.autoSave} setting.
   */
  get autoSave(): boolean {
    return this.getValue().autoSave;
  }

  /**
   * Gets the {@link ApplicationState.autoSaveOnVersioningArchives} setting.
   *
   * @returns True if auto save on versioning archives is enabled.
   */
  get autoSaveOnVersioningArchives(): boolean {
    return this.getValue().autoSaveOnVersioningArchives;
  }

  /**
   * Get the default zoom value.
   *
   * @returns The default zoom.
   */
  get defaultPageZoom(): string {
    return this.getValue().defaultPageZoom;
  }

  /**
   * Get the GSE API config.
   *
   * @returns A GSE API Config.
   */
  get gseConfig(): GseApiConfig {
    return this.getValue().gseApiConfig;
  }

  /**
   * Get the internalViewerClearCacheOnReturnToSearch setting.
   *
   * @returns True if the internal viewer should clear the search cache when returning to search results.
   */
  get internalViewerClearCacheOnReturnToSearch(): boolean {
    return this.getValue().internalViewerClearCacheOnReturnToSearch;
  }

  /**
   * Get the base URL for the instance being used.
   *
   * @returns A string represeting the base URL.
   */
  get instanceUrl(): string {
    return this.getValue().instanceUrl;
  }

  /**
   * Is search count enabled.
   */
  get isSearchCountEnabled(): boolean {
    return this.getValue().searchCountEnabled;
  }

  /**
   * Is the user a readonly user.
   *
   * @returns True if the user is readonly. False if the user is not readonly or if no user is logged in.
   */
  get isReadOnlyLicense(): boolean {
    return this.user?.isReadOnly ?? false;
  }

  /**
   * Get setting for if double clicking a row should open a document.
   *
   * @returns True if documents should be opened by double clicking a row.
   */
  get doubleClickToOpenDocuments(): boolean {
    return this.getValue().doubleClickOpen;
  }

  /**
   * Get the delete search document on insert option.
   *
   * @returns True if the search document should be deleted during the insert.
   */
  get deleteSearchDocumentOnInsert(): boolean {
    return this.getValue().deleteSearchDocumentsOnInsert;
  }

  /**
   * Get the delete inbox document on insert option.
   *
   * @returns True if the inbox document should be deleted during the insert.
   */
  get deleteInboxDocumentOnInsert(): boolean {
    return this.getValue().deleteInboxDocumentOnInsert;
  }

  /**
   * Get the dropdown to typeahead conversion threshold.
   *
   * @returns A number which represents the threshold. A list with more values than this threshold should be converted to a typeahead.
   */
  get dropdownToTypeaheadConversionThreshold(): number {
    return this.getValue().dropdownToTypeaheadConversionThreshold;
  }

  /**
   * Get the state of dxc append to multivalue.
   *
   * @returns True if dxc append to multivalue is enabled.
   */
  get dxcAppendToMultivalue(): boolean {
    return this.getValue().dxcAppendToMultivalue;
  }

  /**
   * Get the state of fxc search system, fields.
   *
   * @returns True if dxc search system fields is enabled.
   */
  get dxcSearchSystemFields(): boolean {
    return this.getValue().dxcSearchSystemFields;
  }

  /**
   * Is the indexer sidebar open.
   *
   * @returns True if the indexer sidebar is open.
   */
  get indexerSidebarOpen(): boolean {
    return this.getValue().indexerSidebarOpen;
  }

  /**
   * Get the keep base search document on insert option.
   *
   * @returns True if the search document should be kept during the insert.
   */
  get keepBaseSearchDocumentOnInsert(): boolean {
    return this.getValue().keepBaseSearchDocumentOnInsert;
  }

  /**
   * Get the keyfreeAutoStartOnDocumentLoad  setting.
   *
   * @returns True if Keyfree should automatically start on document load.
   */
  get keyfreeAutoStartOnDocumentLoad(): boolean {
    return this.getValue().keyfreeAutoStartOnDocumentLoad;
  }

  /**
   * Get the state of system notifications.
   *
   * @returns True if enabled.
   */
  get notifications(): boolean {
    return this.getValue().notifications;
  }

  get openDocument(): OpenDocumentSource | undefined {
    return this.getValue().openDocument;
  }

  /**
   * Gets the state of persisting archive import data.
   *
   * @returns True if enabled.
   */
  get persistArchiveImportData(): boolean {
    return this.getValue().persistArchiveImportData;
  }

  /**
   * Get the PDF Preview URL.
   *
   * @returns Current value of the PDF Preview URL.
   */
  get pdfPreviewUrl(): string {
    return this.getValue().pdfPreviewUrl;
  }

  /**
   * Get the PDF preview ContentSearch term.
   *
   * @returns Current value of the PDF preview ContentSearch term.
   */
  get pdfPreviewContentSearchTerm(): string {
    return this.getValue().pdfPreviewContentSearchTerm;
  }

  /**
   * Get the refreshResultsWhenClosingDocumentTabs setting.
   *
   * @returns True if enabled.
   */
  get refreshResultsWhenClosingDocumentTabs(): boolean {
    return this.getValue().refreshResultsWhenClosingDocumentTabs;
  }

  /**
   * Gets whether annotations should be rendered in the modify thumbnailer.
   *
   * This is not currently used and is set to false. It is here in case we decide
   * we need a user setting to let annotations be rendered in the thumbnailer.
   *
   * @returns True if annotations should be rendered.
   */
  get renderAnnotationsInModifyThumbnailer(): boolean {
    return false;
  }

  /**
   * Gets whether numeric values should be right aligned in the grid.
   *
   * @returns True if numeric values should be right aligned in the grid.
   */
  get rightAlignNumericValuesInGrid(): boolean {
    return this.getValue().rightAlignNumericValuesInGrid;
  }

  /**
   * Gets whether or not to show ContentSearch highlights in the PDF preview viewer if available.
   *
   * @returns True if ContentSearch hits should be shown in the PDF preview viewer.
   */
  get showContentSearchTermsInPdfPreview(): boolean {
    return this.getValue().showContentSearchTermsInPdfPreview;
  }

  /**
   * Gets whether table field columns should be displayed in the grid.
   *
   * @returns True if table field columns should be displayed in the grid.
   */
  get showTableFieldColumnsInGrid(): boolean {
    return this.getValue().showTableFieldColumnsInGrid;
  }

  /**
   * Get the state of whether to show versions archive.
   *
   * @returns True if the versions archive should be shown.
   */
  get showVersionsArchive(): boolean {
    return this.getValue().showVersionsArchive;
  }

  /**
   * Get the theme mode in use.
   *
   * @returns Current value of the theme mode.
   */
  get themeMode(): ThemeMode {
    return this.getValue().themeMode;
  }

  /**
   * Gets whether to use the browser's locale when parsing data.
   *
   * @returns True if the browser's locale should be used to parse data. Otherwise use the application locale.
   */
  get useBrowserLocaleToParseData(): boolean {
    return this.getValue().useBrowserLocaleToParseData;
  }

  /**
   * Whether to use compact mode in the application UI.
   *
   * @returns True if the setting is enabled.
   */
  get useCompactMode(): boolean {
    return this.getValue().useCompactMode;
  }

  /**
   * Whether to use previous search criteria in related search.
   *
   * @returns True if the setting is enabled.
   */
  get usePreviousSearchCriteriaInRelatedSearch(): boolean {
    return this.getValue().usePreviousSearchCriteriaInRelatedSearch;
  }

  /**
   * Get the currently logged in user.
   *
   * @returns A user or undefined if there is no user.
   */
  get user(): User | undefined {
    return this.getValue().user;
  }

  /**
   * Get which user settings have been set by the user.
   *
   * @returns An object where each property represents if the user has set that setting.
   */
  get userUiSettingsSetByUser(): Partial<UserUiSettingsSet> {
    return this.getValue().userUiSettingsSetByUser;
  }

  /**
   * Gets whether the internal viewer should automatically save changes.
   *
   * @returns True if the internal viewer should automatically save changes.
   */
  get viewerAutoSave(): boolean {
    return this.getValue().viewerAutoSave;
  }

  /**
   * Get the width of the viewer right sidebar.
   *
   * @returns A number represeting the width the sidebar should be set to or undefined when not yet set by resizing.
   */
  get viewerRightSidebarWidth(): number | undefined {
    return this.getValue().viewerRightSidebarWidth;
  }

  /**
   * Get the width of the viewer right sidebar when a table field is open.
   *
   * @returns A number represeting the width the sidebar should be set to or undefined when not yet set by resizing.
   */
  get viewerRightSidebarTableFieldWidth(): number | undefined {
    return this.getValue().viewerRightSidebarTableFieldWidth;
  }

  /**
   * Get the current value of if the internal viewer should be used.
   *
   * @returns True if it should be used.
   */
  get viewerUseInternal(): boolean {
    return (
      this.getValue().viewerUseInternal || this.appConfigQuery.hideFullViewer
    );
  }

  /** Get whether to show table cell editor buttons. */
  get showTableCellEditorButtons(): boolean {
    return this.getValue().showTableCellEditorButtons;
  }

  /**
   * Gets the current value of if the internal viewer should be used for imports.
   *
   * @returns True if the internal viewer should be used for import.
   */
  get viewerUseInternalForImport(): boolean {
    return (
      this.getValue().viewerUseInternalForImport ||
      this.appConfigQuery.hideFullViewer
    );
  }

  /**
   * Get favorited search.
   *
   * @param databaseId Database ID.
   * @param searchId Search ID.
   * @returns The user provided search label for the favorite.
   * @throws {AssertionError} If the favorite search's instance ID does not match the current instance ID.
   */
  getFavoritedSearchById(
    databaseId: string,
    searchId: string
  ): FavoriteSearch | undefined {
    const favoriteSearch = this.getFavoritedSearches(databaseId)[searchId];
    if (!favoriteSearch) return undefined;
    assert(
      favoriteSearch.instanceId === undefined ||
        favoriteSearch.instanceId === this.appConfigQuery.instanceId,
      'Favorite search instance ID does not match current instance ID'
    );
    return favoriteSearch;
  }

  /**
   * Get favorited searches.
   *
   * @param databaseId Database ID.
   * @returns A sparse array of favorited searches.
   */
  getFavoritedSearches(databaseId: string): FavoriteDatabaseSearches {
    const favoriteSearchesMap =
      this.getValue().favoritedSearches[databaseId] ?? {};
    // Filter out favorite searches that don't have a matching instance ID.
    const filteredFavoriteSearchesMap: FavoriteDatabaseSearches = {};
    for (const searchId in favoriteSearchesMap) {
      const favoriteSearch = favoriteSearchesMap[searchId];
      if (
        favoriteSearch.instanceId === undefined ||
        favoriteSearch.instanceId === this.appConfigQuery.instanceId
      ) {
        filteredFavoriteSearchesMap[searchId] = favoriteSearch;
      }
    }
    return filteredFavoriteSearchesMap;
  }

  /**
   * Whether the table field grid should be compact.
   *
   * @returns True if the compact table field grid should be used
   */
  getUseCompactTableFieldGrid(): boolean {
    return this.getValue().useCompactTableFieldGrid;
  }

  /**
   * Check if a search is favorited.
   *
   * @param databaseId Database ID.
   * @param searchId Search ID.
   * @returns If the search is favorited.
   */
  isFavoritedSearch(databaseId: string, searchId: string): boolean {
    return !!this.getFavoritedSearchById(databaseId, searchId);
  }

  /**
   * Get whether table fields should be opened in the sidebar.
   *
   * @returns True if table fields should be opened in the sidebar.
   */
  get viewerOpenTableFieldsInSidebar(): boolean {
    return this.getValue().viewerOpenTableFieldsInSidebar;
  }

  /**
   * Adds the dark mode observable.
   */
  private createDarkModeListener() {
    const darkModeMediaQuery = window.matchMedia(
      '(prefers-color-scheme: dark)'
    );
    const systemDarkModeEnabled$ = fromEvent<MediaQueryList>(
      darkModeMediaQuery,
      'change'
    ).pipe(
      startWith(darkModeMediaQuery), // Ensure we get the initial value.
      map((mediaQueryList) => mediaQueryList.matches)
    );

    this.darkMode$ = combineLatest([
      this.themeMode$,
      systemDarkModeEnabled$,
    ]).pipe(
      map(([themeMode, systemDarkModeEnabled]) => {
        this.logger.debug(
          'Dark mode query map hit',
          themeMode,
          systemDarkModeEnabled
        );
        if (themeMode === ThemeMode.system) {
          return systemDarkModeEnabled;
        }

        return themeMode === ThemeMode.dark ? true : false;
      })
    );
  }
}
